import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { shallowEqual, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
    useGetGemsUserBalanceQuery,
    useGetGemsUserCampaignsQuery,
    useGetGemsUserRedemptionsQuery,
    useGetGemsUserRewardsQuery
} from "../../../../../../../../redux/apiSlice";
import { formatDate, formatStatus } from "../../../../../../../util/Helpers";
import { rewardTypeOptions } from "../../../../../../Gems/constants/rewardTypes";

const todayStr = new Date().toISOString().split("T")[0];
const REWARDS_PAGE_SIZE = 10;

const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
};

const getFirstValue = (obj, keys) => {
    if (!obj) return undefined;
    for (const key of keys) {
        const value = obj?.[key];
        if (value !== undefined && value !== null) return value;
    }
    return undefined;
};

const normalizeList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;

    const candidates = [
        value?.data,
        value?.rows,
        value?.results,
        value?.items,
        value?.rewards,
        value?.redemptions,
        value?.campaigns,
        value?.data?.rows,
        value?.data?.results,
        value?.data?.items,
        value?.data?.data
    ];

    for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
    }

    return [];
};

const toDateValue = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const getRowDate = (row) =>
    getFirstValue(row, ["created", "createdAt", "created_at", "timestamp", "date", "updated", "updatedAt", "updated_at"]);

const getRowAmount = (row) =>
    toNumber(
        getFirstValue(row, [
            "amount",
            "gems",
            "gemsAmount",
            "totalGems",
            "total",
            "value",
            "quantity"
        ])
    );

const formatGemsNumber = (value) =>
    value === null || value === undefined ? "-" : Number(value).toLocaleString();

const formatDecimalNumber = (value, maximumFractionDigits = 2) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "-";
    return numericValue.toLocaleString(undefined, { maximumFractionDigits });
};

const formatPercent = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "-";
    return `${formatDecimalNumber(numericValue)}%`;
};

const formatCurrencyAmount = (currency, value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "-";
    return `${currency || "NGN"} ${formatDecimalNumber(numericValue)}`;
};

const titleize = (value) => {
    const raw = value === null || value === undefined ? "" : String(value).trim();
    if (!raw) return "-";

    return raw
        .split("_")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
};

const humanizeKey = (value) =>
    titleize(String(value || "").replace(/([a-z0-9])([A-Z])/g, "$1_$2"));

const isPlainObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);

const getRewardMeta = (row) => (isPlainObject(row?.meta) ? row.meta : {});

const getRewardType = (row) =>
    getFirstValue(row, ["rewardType", "type", "action"]) || getFirstValue(getRewardMeta(row), ["activitySource", "ruleId"]) || "";

const getRewardDescription = (row) =>
    getFirstValue(row, ["description", "narration", "title"]) || "-";

const getRewardSourceRef = (row) =>
    getFirstValue(getRewardMeta(row), ["sourceTransactionRef", "orderId", "accountHistoryId", "courseId"])
    || getFirstValue(row, ["accountHistoryId", "reference", "rewardId", "id"])
    || "-";

const getRewardRuleApplied = (row) =>
    getFirstValue(getRewardMeta(row), ["rewardRuleApplied", "ruleId"]) || "-";

const formatMetaDisplayValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) return value.length ? value.map((item) => formatMetaDisplayValue(item)).join(", ") : "-";
    if (isPlainObject(value)) return JSON.stringify(value);
    return String(value);
};

const getHandledMetaKeys = (rewardType) => {
    const commonKeys = [
        "ruleId",
        "rewardRuleApplied",
        "sourceTransactionRef",
        "gemsTransactionRef",
        "accountHistoryId",
        "activitySource",
    ];

    if (rewardType === "savings") {
        return [
            ...commonKeys,
            "savingsPrincipal",
            "baseAmountNgn",
            "cashbackPercent",
            "rewardPercent",
            "rewardNgn",
            "tenure",
            "savingsCurrency",
        ];
    }

    if (rewardType === "trade") {
        return [
            ...commonKeys,
            "orderId",
            "commissionPercent",
            "rewardPercent",
            "feeTotal",
            "feeCurrency",
            "feeNgn",
            "baseAmountNgn",
            "rewardNgn",
            "isPremium",
        ];
    }

    if (rewardType === "course_completion") {
        return [
            ...commonKeys,
            "courseName",
            "courseId",
            "flatGemsAmount",
            "idempotencyKey",
        ];
    }

    return commonKeys;
};

const buildAdditionalMetaRows = (row) => {
    const meta = getRewardMeta(row);
    const rewardType = getRewardType(row);
    const handledKeys = new Set(getHandledMetaKeys(rewardType));

    return Object.entries(meta)
        .filter(([key]) => !handledKeys.has(key))
        .map(([key, value]) => ({
            label: humanizeKey(key),
            value: formatMetaDisplayValue(value),
        }));
};

const buildRewardDetailRows = (row) => {
    const meta = getRewardMeta(row);
    const rewardType = getRewardType(row);
    const createdAt = getFirstValue(row, ["created", "createdAt", "timestamp", "date"]);
    const updatedAt = getFirstValue(row, ["updated", "updatedAt", "updated_at"]);
    const amount = getRowAmount(row);
    const status = getFirstValue(row, ["status", "state"]) || "";
    const details = [
        { label: "Description", value: getRewardDescription(row) },
        { label: "Reward type", value: titleize(rewardType) },
        { label: "Amount", value: `${formatDecimalNumber(amount)} ${getFirstValue(row, ["currency"]) || "GEMS"}` },
        { label: "Status", value: status ? formatStatus(status) : "-" },
        { label: "Created", value: createdAt ? formatDate(createdAt) : "-" },
        { label: "Updated", value: updatedAt ? formatDate(updatedAt) : "-" },
        { label: "Rule applied", value: getRewardRuleApplied(row) },
        { label: "Source reference", value: getRewardSourceRef(row) },
        { label: "Transaction ref", value: getFirstValue(meta, ["gemsTransactionRef"]) || getFirstValue(row, ["id"]) || "-" },
        { label: "Account history", value: getFirstValue(row, ["accountHistoryId"]) || getFirstValue(meta, ["accountHistoryId"]) || "-" },
    ];

    if (rewardType === "savings") {
        details.push(
            { label: "Savings principal", value: formatCurrencyAmount(meta?.savingsCurrency || "NGN", getFirstValue(meta, ["savingsPrincipal", "baseAmountNgn"])) },
            { label: "Cashback percent", value: formatPercent(meta?.cashbackPercent) },
            { label: "Reward percent", value: formatPercent(meta?.rewardPercent) },
            { label: "Reward value", value: formatCurrencyAmount("NGN", meta?.rewardNgn) },
            { label: "Tenure", value: meta?.tenure || "-" }
        );
    }

    if (rewardType === "trade") {
        details.push(
            { label: "Order ID", value: meta?.orderId || "-" },
            { label: "Commission percent", value: formatPercent(getFirstValue(meta, ["commissionPercent", "rewardPercent"])) },
            { label: "Fee total", value: formatCurrencyAmount(meta?.feeCurrency || "", meta?.feeTotal) },
            { label: "Fee in NGN", value: formatCurrencyAmount("NGN", meta?.feeNgn) },
            { label: "Base amount", value: formatCurrencyAmount("NGN", meta?.baseAmountNgn) },
            { label: "Reward value", value: formatCurrencyAmount("NGN", meta?.rewardNgn) },
            { label: "Premium user", value: typeof meta?.isPremium === "boolean" ? (meta.isPremium ? "Yes" : "No") : "-" }
        );
    }

    if (rewardType === "course_completion") {
        details.push(
            { label: "Course name", value: meta?.courseName || "-" },
            { label: "Course ID", value: meta?.courseId || "-" },
            { label: "Flat Gems amount", value: meta?.flatGemsAmount !== undefined ? `${formatDecimalNumber(meta.flatGemsAmount)} GEMS` : "-" },
            { label: "Idempotency key", value: meta?.idempotencyKey || "-" }
        );
    }

    return details.filter((detail) => detail.value !== undefined && detail.value !== null && detail.value !== "");
};

const truncateMiddle = (value, head = 10, tail = 8) => {
    const str = value === null || value === undefined ? "" : String(value);
    if (str.length <= head + tail + 3) return str;
    return `${str.slice(0, head)}...${str.slice(-tail)}`;
};

function Gems(
    {
        match: {
            params: {
                userId
            }
        }
    }
) {
    const { profile } = useSelector(({ selectedUser }) => {
        const data = selectedUser?.data?.profile?.basic?.userId === userId ? selectedUser.data : null;
        return data || {};
    }, shallowEqual);

    const userGroup = profile?.basic?.userGroup || "";

    const [filters, setFilters] = useState({
        startDate: "",
        endDate: todayStr,
        redemptionStatus: ""
    });

    const [activityView, setActivityView] = useState("rewards");
    const [selectedReward, setSelectedReward] = useState(null);
    const [rewardPage, setRewardPage] = useState(1);

    const commonArgs = useMemo(() => ({
        userId,
        userGroup: userGroup || undefined,
        startDate: filters.startDate || "",
        endDate: filters.endDate || ""
    }), [userId, userGroup, filters.startDate, filters.endDate]);

    const { data: balanceData, isFetching: isFetchingBalance, error: balanceError } =
        useGetGemsUserBalanceQuery({ userId, userGroup: userGroup || undefined }, { skip: !userId });

    const { data: rewardsData, isFetching: isFetchingRewards, error: rewardsError } =
        useGetGemsUserRewardsQuery(commonArgs, { skip: !userId });

    const { data: redemptionsData, isFetching: isFetchingRedemptions, error: redemptionsError } =
        useGetGemsUserRedemptionsQuery(
            {
                ...commonArgs,
                status: filters.redemptionStatus || undefined,
                limit: 50,
                offset: 0
            },
            { skip: !userId }
        );

    const { data: campaignsData, isFetching: isFetchingCampaigns, error: campaignsError } =
        useGetGemsUserCampaignsQuery({ userId, userGroup: userGroup || undefined }, { skip: !userId });

    const isFetching = isFetchingBalance || isFetchingRewards || isFetchingRedemptions || isFetchingCampaigns;

    const onFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const balanceAccount = balanceData?.data || balanceData || null;
    const cashBalance = toNumber(getFirstValue(balanceAccount, ["cashBalance", "availableForWithdrawal", "tradingPower"]));
    const availableForWithdrawal = toNumber(getFirstValue(balanceAccount, ["availableForWithdrawal"]));
    const balanceCurrency = getFirstValue(balanceAccount, ["currency"]) || "GEMS";
    const balanceStatus = getFirstValue(balanceAccount, ["status"]) || "";
    const userDisplayName =
        getFirstValue(balanceAccount, ["username"]) || getFirstValue(profile?.basic, ["username", "name"]) || userId;

    const rewards = useMemo(() => normalizeList(rewardsData), [rewardsData]);
    const redemptions = useMemo(() => normalizeList(redemptionsData), [redemptionsData]);
    const campaigns = useMemo(() => normalizeList(campaignsData), [campaignsData]);

    const rewardSummary = useMemo(() => {
        const total = rewards.reduce((sum, row) => sum + (getRowAmount(row) ?? 0), 0);
        const lastAt = rewards
            .map((r) => toDateValue(getRowDate(r)))
            .filter(Boolean)
            .sort((a, b) => b - a)[0];
        return { total, count: rewards.length, lastAt };
    }, [rewards]);

    const redemptionSummary = useMemo(() => {
        const isSuccessful = (status) => String(status || "").toLowerCase() === "success";
        const totalSuccessful = redemptions
            .filter((row) => isSuccessful(getFirstValue(row, ["status", "state"])))
            .reduce((sum, row) => sum + (getRowAmount(row) ?? 0), 0);
        const lastAt = redemptions
            .map((r) => toDateValue(getRowDate(r)))
            .filter(Boolean)
            .sort((a, b) => b - a)[0];
        return { totalSuccessful, count: redemptions.length, lastAt };
    }, [redemptions]);

    const lastActivityAt = useMemo(() => {
        const dates = [rewardSummary.lastAt, redemptionSummary.lastAt].filter(Boolean);
        return dates.sort((a, b) => b - a)[0] ?? null;
    }, [rewardSummary.lastAt, redemptionSummary.lastAt]);

    const recentRewards = useMemo(() => {
        return [...rewards]
            .map((row) => ({ row, date: toDateValue(getRowDate(row)) }))
            .sort((a, b) => (b.date?.getTime?.() ?? 0) - (a.date?.getTime?.() ?? 0))
            .map(({ row }) => row);
    }, [rewards]);

    const rewardTotalPages = useMemo(
        () => Math.max(1, Math.ceil(recentRewards.length / REWARDS_PAGE_SIZE)),
        [recentRewards.length]
    );

    useEffect(() => {
        setRewardPage(1);
    }, [filters.startDate, filters.endDate, userId]);

    useEffect(() => {
        if (rewardPage > rewardTotalPages) {
            setRewardPage(rewardTotalPages);
        }
    }, [rewardPage, rewardTotalPages]);

    const pagedRewards = useMemo(() => {
        const start = (Math.max(1, rewardPage) - 1) * REWARDS_PAGE_SIZE;
        return recentRewards.slice(start, start + REWARDS_PAGE_SIZE);
    }, [recentRewards, rewardPage]);

    const rewardTypeSummary = useMemo(() => {
        const grouped = rewardTypeOptions.reduce((acc, rewardType) => {
            acc[rewardType] = {
                rewardType,
                total: 0,
                count: 0,
            };
            return acc;
        }, {});

        rewards.forEach((row) => {
            const rewardType = getRewardType(row);
            if (!grouped[rewardType]) return;

            grouped[rewardType].count += 1;
            grouped[rewardType].total += getRowAmount(row) ?? 0;
        });

        return rewardTypeOptions.map((rewardType) => grouped[rewardType]);
    }, [rewards]);

    const recentRedemptions = useMemo(() => {
        return [...redemptions]
            .map((row) => ({ row, date: toDateValue(getRowDate(row)) }))
            .sort((a, b) => (b.date?.getTime?.() ?? 0) - (a.date?.getTime?.() ?? 0))
            .slice(0, 20)
            .map(({ row }) => row);
    }, [redemptions]);

    return (
        <div className="py-4 px-6">
            <div className="mb-4">
                <h2 className="font-weight-bolder mb-1">Gems</h2>
                <div className="text-muted mb-4">
                    User:{" "}
                    <Link to={`/users/view/${userId}/gems`} className="text-hover-primary" title={userId}>
                        {userDisplayName}
                    </Link>
                    {userGroup ? ` • ${userGroup}` : ""}
                </div>
                <div className="d-flex flex-wrap align-items-end">
                    <div className="mr-4 mb-2" style={{ minWidth: 200 }}>
                        <label className="text-muted font-size-sm mb-1">Start date</label>
                        <input
                            type="date"
                            className="form-control"
                            name="startDate"
                            value={filters.startDate}
                            onChange={onFilterChange}
                        />
                    </div>
                    <div className="mb-2" style={{ minWidth: 200 }}>
                        <label className="text-muted font-size-sm mb-1">End date</label>
                        <input
                            type="date"
                            className="form-control"
                            name="endDate"
                            value={filters.endDate}
                            onChange={onFilterChange}
                        />
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-xl-12">
                    <div className="card card-custom gutter-b">
                        <div className="card-header">
                            <div className="card-title">
                                <h3 className="card-label">Balance &amp; rewards</h3>
                            </div>
                        </div>
                        <div className="card-body py-6">
                            <div className="row">
                                <div className="col-md-7">
                                    <div className="text-muted font-size-sm mb-3">Balance</div>
                                    {isFetchingBalance ? (
                                        <div className="text-muted">Loading...</div>
                                    ) : balanceError ? (
                                        <div className="text-danger">Failed to load balance</div>
                                    ) : (
                                        <>
                                            <div className="d-flex justify-content-between align-items-start mb-5">
                                                <div className="font-size-h1 font-weight-bolder">
                                                    {formatGemsNumber(cashBalance)}
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-muted font-size-sm">{balanceCurrency}</div>
                                                    {balanceStatus ? <div>{formatStatus(balanceStatus)}</div> : null}
                                                </div>
                                            </div>
                                            <div className="d-flex justify-content-between py-2">
                                                <span className="text-muted">Available</span>
                                                <span className="font-weight-bolder">
                                                    {formatGemsNumber(availableForWithdrawal)}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="col-md-5">
                                    <div
                                        className="h-100 pt-6 pt-md-0"
                                        style={{ borderLeft: "1px dashed #E4E6EF", paddingLeft: 28 }}
                                    >
                                        <div className="text-muted font-size-sm mb-3">Rewards</div>
                                        {isFetchingRewards ? (
                                            <div className="text-muted">Loading...</div>
                                        ) : rewardsError ? (
                                            <div className="text-danger">Failed to load rewards</div>
                                        ) : (
                                            <>
                                                <div className="font-size-h1 font-weight-bolder mb-2">
                                                    {formatGemsNumber(rewardSummary.total)}
                                                </div>
                                                <div className="d-flex justify-content-between py-2 border-bottom">
                                                    <span className="text-muted">Records</span>
                                                    <span className="font-weight-bolder">
                                                        {rewardSummary.count.toLocaleString()}
                                                    </span>
                                                </div>
                                                <div className="d-flex justify-content-between py-2">
                                                    <span className="text-muted">Last reward</span>
                                                    <span className="font-weight-bolder">
                                                        {rewardSummary.lastAt ? formatDate(rewardSummary.lastAt.toISOString()) : "—"}
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-12">
                    <div className="card card-custom gutter-b">
                        <div className="card-header">
                            <div className="card-title">
                                <h3 className="card-label">Redemptions</h3>
                            </div>
                        </div>
                        <div className="card-body">
                            {isFetchingRedemptions ? (
                                <div className="text-muted">Loading...</div>
                            ) : redemptionsError ? (
                                <div className="text-danger">Failed to load redemptions</div>
                            ) : (
                                <>
                                    <div className="font-size-h2 font-weight-bolder">
                                        {formatGemsNumber(redemptionSummary.totalSuccessful)}
                                    </div>
                                    <div className="text-muted">
                                        {redemptionSummary.count.toLocaleString()} record
                                        {redemptionSummary.count === 1 ? "" : "s"} (total)
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-xl-12">
                    <div className="card card-custom gutter-b">
                        <div className="card-header">
                            <div className="card-title">
                                <h3 className="card-label">Last activity</h3>
                            </div>
                        </div>
                        <div className="card-body">
                            {isFetching ? (
                                <div className="text-muted">Loading...</div>
                            ) : lastActivityAt ? (
                                <div className="font-weight-bolder">{formatDate(lastActivityAt.toISOString())}</div>
                            ) : (
                                <div className="text-muted">No activity found</div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-xl-12">
                    <div className="card card-custom gutter-b">
                        <div className="card-header">
                            <div className="card-title">
                                <h3 className="card-label">Activity</h3>
                            </div>
                            <div className="card-toolbar">
                                <div className="d-flex flex-wrap align-items-center justify-content-end">
                                    <div className="mr-3 mb-2 mb-md-0" style={{ minWidth: 200 }}>
                                        <select
                                            className="form-control form-control-sm"
                                            value={activityView}
                                            onChange={(e) => setActivityView(e.target.value)}
                                        >
                                            <option value="rewards">
                                                Rewards ({rewardSummary.count.toLocaleString()})
                                            </option>
                                            <option value="redemptions">
                                                Redemptions ({redemptionSummary.count.toLocaleString()})
                                            </option>
                                            <option value="campaigns">
                                                Campaigns ({campaigns.length.toLocaleString()})
                                            </option>
                                        </select>
                                    </div>

                                    {activityView === "redemptions" ? (
                                        <div style={{ minWidth: 200 }}>
                                            <select
                                                className="form-control form-control-sm"
                                                name="redemptionStatus"
                                                value={filters.redemptionStatus}
                                                onChange={onFilterChange}
                                            >
                                                <option value="">All statuses</option>
                                                <option value="completed">Completed</option>
    
                                                <option value="failed">Failed</option>
                                            </select>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>

                        <div className="card-body py-6">
                            {activityView === "rewards" ? (
                                <>
                                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
                                        <div className="text-muted">
                                            Showing {recentRewards.length ? (((rewardPage - 1) * REWARDS_PAGE_SIZE) + 1).toLocaleString() : 0}-
                                            {Math.min(rewardPage * REWARDS_PAGE_SIZE, recentRewards.length).toLocaleString()} of{" "}
                                            {rewardSummary.count.toLocaleString()} rewards
                                        </div>
                                        <div className="text-muted">
                                            {rewardSummary.lastAt ? <>Last reward: {formatDate(rewardSummary.lastAt.toISOString())}</> : null}
                                        </div>
                                    </div>
                                    {!isFetchingRewards && !rewardsError && rewardTypeSummary.length ? (
                                        <div className="row mb-5">
                                            {rewardTypeSummary.map((entry) => (
                                                <div key={entry.rewardType} className="col-md-4 col-lg-3 mb-4">
                                                    <div className="bg-light rounded px-4 py-3 h-100">
                                                        <div className="text-muted font-size-sm mb-1">
                                                            {titleize(entry.rewardType)}
                                                        </div>
                                                        <div className="font-weight-bolder font-size-h4 mb-1">
                                                            {formatGemsNumber(entry.total)}
                                                        </div>
                                                        <div className="text-muted font-size-sm">
                                                            {entry.count.toLocaleString()} reward{entry.count === 1 ? "" : "s"}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : null}
                                    {rewardsError ? <div className="text-danger mb-4">Failed to load rewards</div> : null}
                                    {!isFetchingRewards && !rewardsError && recentRewards.length === 0 ? (
                                        <div className="text-muted">No rewards found for the selected range.</div>
                                    ) : (
                                        <>
                                            <div className="table-responsive">
                                                <table className="table table-sm table-head-custom">
                                                    <thead>
                                                        <tr>
                                                            <th>Created</th>
                                                            <th>Reward</th>
                                                            <th>Type</th>
                                                            <th>Rule applied</th>
                                                            <th>Source</th>
                                                            <th className="text-right">Amount</th>
                                                            <th>Status</th>
                                                            <th className="text-right">Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {isFetchingRewards ? (
                                                            <tr>
                                                                <td colSpan={8} className="text-center text-muted py-5">
                                                                    Loading...
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            pagedRewards.map((row, idx) => {
                                                                const createdAt = getRowDate(row);
                                                                const description = getRewardDescription(row);
                                                                const type = getRewardType(row);
                                                                const amount = getRowAmount(row);
                                                                const status = getFirstValue(row, ["status", "state"]) ?? "";
                                                                const ruleApplied = getRewardRuleApplied(row);
                                                                const sourceRef = getRewardSourceRef(row);

                                                                return (
                                                                    <tr
                                                                        key={
                                                                            getFirstValue(row, ["id", "rewardId", "_id"]) ??
                                                                            `${rewardPage}-${idx}`
                                                                        }
                                                                    >
                                                                        <td>{createdAt ? formatDate(createdAt) : "-"}</td>
                                                                        <td className="text-muted" title={String(description)}>
                                                                            {truncateMiddle(description, 36, 18)}
                                                                        </td>
                                                                        <td className="text-muted">{titleize(type)}</td>
                                                                        <td className="text-muted" title={String(ruleApplied)}>
                                                                            {truncateMiddle(ruleApplied, 30, 14)}
                                                                        </td>
                                                                        <td className="text-muted" title={String(sourceRef)}>
                                                                            {truncateMiddle(sourceRef)}
                                                                        </td>
                                                                        <td className="text-right">{formatGemsNumber(amount)}</td>
                                                                        <td>{formatStatus(status)}</td>
                                                                        <td className="text-right">
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-icon btn-light-primary btn-sm"
                                                                                onClick={() => setSelectedReward(row)}
                                                                                title="View reward details"
                                                                            >
                                                                                <i className="fa fa-eye" />
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div className="d-flex justify-content-end align-items-center mt-4">
                                                <button
                                                    type="button"
                                                    className="btn btn-light-primary btn-sm mr-2"
                                                    onClick={() => setRewardPage((page) => Math.max(1, page - 1))}
                                                    disabled={rewardPage <= 1}
                                                >
                                                    Prev
                                                </button>
                                                <div className="text-muted font-size-sm mr-2">
                                                    Page {rewardPage} of {rewardTotalPages}
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn btn-light-primary btn-sm"
                                                    onClick={() => setRewardPage((page) => Math.min(rewardTotalPages, page + 1))}
                                                    disabled={rewardPage >= rewardTotalPages}
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </>
                            ) : null}

                            {activityView === "redemptions" ? (
                                <>
                                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
                                        <div className="text-muted">
                                            Showing latest {Math.min(20, redemptionSummary.count).toLocaleString()} redemptions
                                        </div>
                                        <div className="text-muted">
                                            {redemptionSummary.lastAt
                                                ? `Last: ${formatDate(redemptionSummary.lastAt.toISOString())}`
                                                : ""}
                                        </div>
                                    </div>
                                    {redemptionsError ? (
                                        <div className="text-danger mb-4">Failed to load redemptions</div>
                                    ) : null}
                                    {!isFetchingRedemptions && !redemptionsError && recentRedemptions.length === 0 ? (
                                        <div className="text-muted">No redemptions found for the selected range.</div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-sm table-head-custom">
                                                <thead>
                                                    <tr>
                                                        <th>Created</th>
                                                        <th>Reward</th>
                                                        <th className="text-right">Amount</th>
                                                        <th>Status</th>
                                                        <th>Reference</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {isFetchingRedemptions ? (
                                                        <tr>
                                                            <td colSpan={5} className="text-center text-muted py-5">
                                                                Loading...
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        recentRedemptions.map((row, idx) => {
                                                            const createdAt = getRowDate(row);
                                                            const reward =
                                                                getFirstValue(row, [
                                                                    "rewardName",
                                                                    "rewardType",
                                                                    "reward",
                                                                    "type",
                                                                    "productName",
                                                                ]) ??
                                                                getFirstValue(row?.reward, ["name", "type", "id"]) ??
                                                                "-";
                                                            const amount = getRowAmount(row);
                                                            const status = getFirstValue(row, ["status", "state"]) ?? "";
                                                            const ref =
                                                                getFirstValue(row, [
                                                                    "reference",
                                                                    "referenceId",
                                                                    "redemptionId",
                                                                    "redemption_id",
                                                                    "orderId",
                                                                    "order_id",
                                                                    "id",
                                                                    "_id",
                                                                ]) ?? "-";

                                                            return (
                                                                <tr
                                                                    key={
                                                                        getFirstValue(row, ["id", "redemptionId", "_id"]) ??
                                                                        `${idx}`
                                                                    }
                                                                >
                                                                    <td>{createdAt ? formatDate(createdAt) : "-"}</td>
                                                                    <td className="text-muted" title={String(reward)}>
                                                                        {truncateMiddle(reward)}
                                                                    </td>
                                                                    <td className="text-right">{formatGemsNumber(amount)}</td>
                                                                    <td>{formatStatus(status)}</td>
                                                                    <td className="text-muted" title={String(ref)}>
                                                                        {truncateMiddle(ref)}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </>
                            ) : null}

                            {activityView === "campaigns" ? (
                                <>
                                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
                                        <div className="text-muted">
                                            Showing {Math.min(50, campaigns.length).toLocaleString()} campaigns
                                        </div>
                                    </div>
                                    {campaignsError ? <div className="text-danger mb-4">Failed to load campaigns</div> : null}
                                    {!isFetchingCampaigns && !campaignsError && campaigns.length === 0 ? (
                                        <div className="text-muted">No campaigns found for this user.</div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-sm table-head-custom">
                                                <thead>
                                                    <tr>
                                                        <th>Campaign</th>
                                                        <th className="text-right">Accrued</th>
                                                        <th className="text-right">Redeemed</th>
                                                        <th className="text-right">Remaining</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {isFetchingCampaigns ? (
                                                        <tr>
                                                            <td colSpan={5} className="text-center text-muted py-5">
                                                                Loading...
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        campaigns.slice(0, 50).map((row, idx) => {
                                                            const name =
                                                                getFirstValue(row, [
                                                                    "campaignName",
                                                                    "name",
                                                                    "campaignId",
                                                                    "campaign_id",
                                                                    "id",
                                                                    "_id",
                                                                ]) ??
                                                                getFirstValue(row?.campaign, ["name", "id"]) ??
                                                                "-";
                                                            const accrued = toNumber(
                                                                getFirstValue(row, [
                                                                    "totalAccrued",
                                                                    "accrued",
                                                                    "earned",
                                                                    "totalEarned",
                                                                    "gemsAccrued",
                                                                    "gemsEarned",
                                                                ])
                                                            );
                                                            const redeemed = toNumber(
                                                                getFirstValue(row, [
                                                                    "totalRedeemed",
                                                                    "redeemed",
                                                                    "spent",
                                                                    "totalSpent",
                                                                    "gemsRedeemed",
                                                                    "gemsSpent",
                                                                ])
                                                            );
                                                            const balance =
                                                                toNumber(getFirstValue(row, ["balance", "remaining", "available"])) ??
                                                                (accrued !== null && redeemed !== null ? accrued - redeemed : null);
                                                            const status = getFirstValue(row, ["status", "state"]) ?? "";

                                                            return (
                                                                <tr
                                                                    key={
                                                                        getFirstValue(row, ["id", "campaignId", "_id"]) ??
                                                                        `${idx}`
                                                                    }
                                                                >
                                                                    <td className="text-muted" title={String(name)}>
                                                                        {truncateMiddle(name)}
                                                                    </td>
                                                                    <td className="text-right">{formatGemsNumber(accrued)}</td>
                                                                    <td className="text-right">{formatGemsNumber(redeemed)}</td>
                                                                    <td className="text-right">{formatGemsNumber(balance)}</td>
                                                                    <td>{formatStatus(status)}</td>
                                                                </tr>
                                                            );
                                                        })
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
            <Modal
                show={Boolean(selectedReward)}
                onHide={() => setSelectedReward(null)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Reward details</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
                    {selectedReward ? (
                        <>
                            <div className="mb-5">
                                <div className="text-muted text-uppercase font-size-xs mb-2">Reward summary</div>
                                <div className="font-size-h4 font-weight-bolder mb-1">
                                    {getRewardDescription(selectedReward)}
                                </div>
                                <div className="text-muted">
                                    {titleize(getRewardType(selectedReward))} • {formatGemsNumber(getRowAmount(selectedReward))}{" "}
                                    {getFirstValue(selectedReward, ["currency"]) || "GEMS"}
                                </div>
                            </div>

                            <div className="row">
                                {buildRewardDetailRows(selectedReward).map((detail) => (
                                    <div key={detail.label} className="col-md-6 mb-4">
                                        <div className="text-muted font-size-xs text-uppercase mb-1">{detail.label}</div>
                                        <div className="font-weight-bolder" style={{ wordBreak: "break-word" }}>
                                            {detail.value}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {buildAdditionalMetaRows(selectedReward).length ? (
                                <>
                                    <div className="separator separator-dashed my-4" />
                                    <div className="text-muted text-uppercase font-size-xs mb-3">Additional metadata</div>
                                    <div className="row">
                                        {buildAdditionalMetaRows(selectedReward).map((detail) => (
                                            <div key={detail.label} className="col-md-6 mb-4">
                                                <div className="text-muted font-size-xs text-uppercase mb-1">{detail.label}</div>
                                                <div className="font-weight-bolder" style={{ wordBreak: "break-word" }}>
                                                    {detail.value}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : null}

                            <div className="separator separator-dashed my-4" />

                            <div className="text-muted text-uppercase font-size-xs mb-2">Raw metadata</div>
                            <pre
                                className="bg-light rounded p-4 mb-0"
                                style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}
                            >
                                {JSON.stringify(getRewardMeta(selectedReward), null, 2)}
                            </pre>
                        </>
                    ) : null}
                </Modal.Body>
            </Modal>
        </div>
    );
}

export default Gems;
